import { PHONE_COUNTRY_NATIONAL_LENGTHS } from './phone-lengths';

const PHONE_GROUP_SIZE = 3;
const DEFAULT_PHONE_COUNTRY = 'PT';

export const PHONE_COUNTRY_CALLING_CODES = {
  AD: '376',
  AE: '971',
  AF: '93',
  AG: '1',
  AI: '1',
  AL: '355',
  AM: '374',
  AO: '244',
  AR: '54',
  AT: '43',
  AU: '61',
  AW: '297',
  AZ: '994',
  BA: '387',
  BB: '1',
  BD: '880',
  BE: '32',
  BF: '226',
  BG: '359',
  BH: '973',
  BI: '257',
  BJ: '229',
  BM: '1',
  BN: '673',
  BO: '591',
  BR: '55',
  BS: '1',
  BT: '975',
  BW: '267',
  BY: '375',
  BZ: '501',
  CA: '1',
  CD: '243',
  CF: '236',
  CG: '242',
  CH: '41',
  CI: '225',
  CK: '682',
  CL: '56',
  CM: '237',
  CN: '86',
  CO: '57',
  CR: '506',
  CU: '53',
  CV: '238',
  CY: '357',
  CZ: '420',
  DE: '49',
  DJ: '253',
  DK: '45',
  DM: '1',
  DO: '1',
  DZ: '213',
  EC: '593',
  EE: '372',
  EG: '20',
  ER: '291',
  ES: '34',
  ET: '251',
  FI: '358',
  FJ: '679',
  FM: '691',
  FO: '298',
  FR: '33',
  GA: '241',
  GB: '44',
  GD: '1',
  GE: '995',
  GH: '233',
  GI: '350',
  GL: '299',
  GM: '220',
  GN: '224',
  GQ: '240',
  GR: '30',
  GT: '502',
  GW: '245',
  GY: '592',
  HK: '852',
  HN: '504',
  HR: '385',
  HT: '509',
  HU: '36',
  ID: '62',
  IE: '353',
  IL: '972',
  IM: '44',
  IN: '91',
  IQ: '964',
  IR: '98',
  IS: '354',
  IT: '39',
  JM: '1',
  JO: '962',
  JP: '81',
  KE: '254',
  KG: '996',
  KH: '855',
  KI: '686',
  KM: '269',
  KN: '1',
  KP: '850',
  KR: '82',
  KW: '965',
  KY: '1',
  KZ: '7',
  LA: '856',
  LB: '961',
  LC: '1',
  LI: '423',
  LK: '94',
  LR: '231',
  LS: '266',
  LT: '370',
  LU: '352',
  LV: '371',
  LY: '218',
  MA: '212',
  MC: '377',
  MD: '373',
  ME: '382',
  MG: '261',
  MK: '389',
  ML: '223',
  MM: '95',
  MN: '976',
  MO: '853',
  MR: '222',
  MT: '356',
  MU: '230',
  MV: '960',
  MW: '265',
  MX: '52',
  MY: '60',
  MZ: '258',
  NA: '264',
  NE: '227',
  NG: '234',
  NI: '505',
  NL: '31',
  NO: '47',
  NP: '977',
  NZ: '64',
  OM: '968',
  PA: '507',
  PE: '51',
  PG: '675',
  PH: '63',
  PK: '92',
  PL: '48',
  PR: '1',
  PT: '351',
  PY: '595',
  QA: '974',
  RO: '40',
  RS: '381',
  RU: '7',
  RW: '250',
  SA: '966',
  SB: '677',
  SC: '248',
  SD: '249',
  SE: '46',
  SG: '65',
  SI: '386',
  SK: '421',
  SL: '232',
  SM: '378',
  SN: '221',
  SO: '252',
  SR: '597',
  SS: '211',
  ST: '239',
  SV: '503',
  SY: '963',
  SZ: '268',
  TD: '235',
  TG: '228',
  TH: '66',
  TJ: '992',
  TL: '670',
  TM: '993',
  TN: '216',
  TO: '676',
  TR: '90',
  TT: '1',
  TV: '688',
  TW: '886',
  TZ: '255',
  UA: '380',
  UG: '256',
  US: '1',
  UY: '598',
  UZ: '998',
  VA: '39',
  VC: '1',
  VE: '58',
  VN: '84',
  VU: '678',
  WS: '685',
  YE: '967',
  ZA: '27',
  ZM: '260',
  ZW: '263',
} as const;

export type PhoneCountryCode = keyof typeof PHONE_COUNTRY_CALLING_CODES;

const COMMON_PHONE_COUNTRIES: PhoneCountryCode[] = [
  'PT',
  'ES',
  'GB',
  'US',
  'CA',
  'FR',
  'IT',
  'DE',
  'NL',
  'BE',
  'CH',
  'IE',
];

const groupDigits = (digits: string): string =>
  (digits.match(new RegExp(`.{1,${PHONE_GROUP_SIZE}}`, 'g')) ?? []).join(' ');

const digitsOnly = (value: string): string => value.replace(/\D/g, '');

const stripSelectedCallingCode = (
  digits: string,
  countryCode: PhoneCountryCode
): string => {
  const callingCode = PHONE_COUNTRY_CALLING_CODES[countryCode];
  return digits.startsWith(callingCode)
    ? digits.slice(callingCode.length)
    : digits;
};

export const normalizePhoneInput = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';

  const hasLeadingPlus = trimmed.startsWith('+');
  const digits = digitsOnly(trimmed);
  if (digits.length === 0) return hasLeadingPlus ? '+' : '';

  return `${hasLeadingPlus ? '+' : ''}${digits}`;
};

export const formatPhoneInput = (value: string): string => {
  const normalized = normalizePhoneInput(value);
  if (normalized.length === 0) return '';
  if (normalized === '+') return '+';

  const hasLeadingPlus = normalized.startsWith('+');
  const digits = hasLeadingPlus ? normalized.slice(1) : normalized;
  const grouped = groupDigits(digits);

  return hasLeadingPlus ? `+${grouped}` : grouped;
};

export const sortPhoneCountryCodes = (
  countryCodes: PhoneCountryCode[]
): PhoneCountryCode[] => {
  const commonOrder = new Map(
    COMMON_PHONE_COUNTRIES.map((countryCode, index) => [countryCode, index])
  );

  return [...countryCodes].sort((a, b) => {
    const aCommon = commonOrder.get(a);
    const bCommon = commonOrder.get(b);

    if (aCommon !== undefined || bCommon !== undefined) {
      return (
        (aCommon ?? Number.MAX_SAFE_INTEGER) -
        (bCommon ?? Number.MAX_SAFE_INTEGER)
      );
    }

    return a.localeCompare(b);
  });
};

export const isPhoneCountryCode = (value: string): value is PhoneCountryCode =>
  value in PHONE_COUNTRY_CALLING_CODES;

export const getPhoneCallingCode = (countryCode: PhoneCountryCode): string =>
  PHONE_COUNTRY_CALLING_CODES[countryCode];

export const getPossiblePhoneNationalLengths = (
  countryCode: PhoneCountryCode
): readonly number[] => PHONE_COUNTRY_NATIONAL_LENGTHS[countryCode] ?? [];

export const getMaxPhoneNationalLength = (
  countryCode: PhoneCountryCode
): number => {
  const lengths = getPossiblePhoneNationalLengths(countryCode);
  return lengths.length > 0 ? Math.max(...lengths) : 15;
};

export const getPhoneLengthLabel = (countryCode: PhoneCountryCode): string =>
  getPossiblePhoneNationalLengths(countryCode).join(', ');

export const inferPhoneCountry = (value: string): PhoneCountryCode => {
  const normalized = normalizePhoneInput(value);
  if (!normalized.startsWith('+') || normalized === '+') {
    return DEFAULT_PHONE_COUNTRY;
  }

  const digits = normalized.slice(1);
  const matches = sortPhoneCountryCodes(
    (Object.keys(PHONE_COUNTRY_CALLING_CODES) as PhoneCountryCode[]).filter(
      (countryCode) =>
        digits.startsWith(PHONE_COUNTRY_CALLING_CODES[countryCode])
    )
  );

  return (
    [...matches].sort((a, b) => {
      const codeDelta =
        PHONE_COUNTRY_CALLING_CODES[b].length -
        PHONE_COUNTRY_CALLING_CODES[a].length;
      return codeDelta === 0
        ? matches.indexOf(a) - matches.indexOf(b)
        : codeDelta;
    })[0] ?? DEFAULT_PHONE_COUNTRY
  );
};

export const phoneValueUsesCountry = (
  value: string,
  countryCode: PhoneCountryCode
): boolean => {
  const normalized = normalizePhoneInput(value);
  if (!normalized.startsWith('+') || normalized === '+') return true;
  return normalized
    .slice(1)
    .startsWith(PHONE_COUNTRY_CALLING_CODES[countryCode]);
};

export const localPhoneFromValue = (
  value: string,
  countryCode: PhoneCountryCode
): string => {
  const normalized = normalizePhoneInput(value);
  const digits = digitsOnly(normalized);
  const localDigits = normalized.startsWith('+')
    ? stripSelectedCallingCode(digits, countryCode)
    : digits;

  return groupDigits(localDigits);
};

export const isPhoneLengthValid = (
  countryCode: PhoneCountryCode,
  value: string
): boolean => {
  const localDigits = digitsOnly(localPhoneFromValue(value, countryCode));
  if (localDigits.length === 0) return true;
  return getPossiblePhoneNationalLengths(countryCode).includes(
    localDigits.length
  );
};

export const trimLocalPhoneDigitsToCountry = (
  countryCode: PhoneCountryCode,
  value: string
): string => digitsOnly(value).slice(0, getMaxPhoneNationalLength(countryCode));

export const phoneValueFromCountryAndLocalInput = (
  countryCode: PhoneCountryCode,
  localInput: string
): string => {
  const normalized = normalizePhoneInput(localInput);
  if (normalized.startsWith('+')) return normalized;

  const localDigits = trimLocalPhoneDigitsToCountry(countryCode, normalized);
  if (localDigits.length === 0) return '';

  return `+${PHONE_COUNTRY_CALLING_CODES[countryCode]}${localDigits}`;
};

export const replacePhoneCountry = (
  value: string,
  fromCountryCode: PhoneCountryCode,
  toCountryCode: PhoneCountryCode
): string => {
  const localDigits = trimLocalPhoneDigitsToCountry(
    toCountryCode,
    localPhoneFromValue(value, fromCountryCode)
  );
  if (localDigits.length === 0) return '';

  return `+${PHONE_COUNTRY_CALLING_CODES[toCountryCode]}${localDigits}`;
};
